import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, XCircle, Package, User, MapPin, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { useToast } from '../ui/ToastContext';
import ViewLayout from '../ui/ViewLayout';

const ReservationsView = ({ 
  currentUser, 
  fetchReservations, 
  updateReservationStatus, 
  completeReservation,
  props = [], // Add props to get current status
  onDataChange, // Add callback to refresh main app data
  onNotifyUser // Add callback to trigger notifications
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('pending');
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [expandedReservations, setExpandedReservations] = useState(new Set());
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedCompletionReservation, setSelectedCompletionReservation] = useState(null);

  // Load data when component mounts or user changes
  useEffect(() => {
    loadAllReservations();
  }, [currentUser]);

  // Load all reservations once
  const loadAllReservations = async () => {
    try {
      setLoading(true);
      console.log('ReservationsView: Loading all reservations for:', {
        currentUser: currentUser,
        role: currentUser.role,
        organization: currentUser.organization
      });
      // Fetch all reservations (no status filter)
      const data = await fetchReservations(currentUser, null);
      console.log('ReservationsView: Loaded all reservations:', data);
      setReservations(data);
    } catch (err) {
      console.error('Error loading reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter reservations based on active tab
  const getFilteredReservations = () => {
    switch (activeTab) {
      case 'pending':
        return reservations.filter(r => r.status === 'pending');
      case 'ready':
        return reservations.filter(r => r.status === 'approved');
      case 'active':
        return reservations.filter(r => r.status === 'active_checkout');
      case 'history':
        return reservations.filter(r => 
          ['completed', 'rejected', 'cancelled'].includes(r.status)
        );
      default:
        return reservations;
    }
  };

  // Handle reservation actions
  const handleApprove = async (reservationId, approvalData) => {
    try {
      await updateReservationStatus(reservationId, 'approved', approvalData);
      
      // Find the reservation to get details for notification
      const reservation = reservations.find(r => r.id === reservationId);
      if (reservation && onNotifyUser) {
        onNotifyUser('reservation_approved', {
          propCount: reservation.reservation_items?.length || 0,
          orgName: currentUser.organization,
          pickupDate: approvalData.approved_pickup_date,
          targetUser: reservation.requested_by_name // Pass the requesting user
        });
      }
      
      toast.success('Reservation approved successfully!');
      await loadAllReservations(); // Reload all data
      setShowApprovalModal(false);
      setSelectedReservation(null);
    } catch (err) {
      toast.error('Failed to approve reservation', { title: 'Approval Error' });
    }
  };

  const handleReject = async (reservationId, reason) => {
    try {
      await updateReservationStatus(reservationId, 'rejected', { admin_notes: reason });
      
      // Find the reservation to get details for notification
      const reservation = reservations.find(r => r.id === reservationId);
      if (reservation && onNotifyUser) {
        onNotifyUser('reservation_rejected', {
          propCount: reservation.reservation_items?.length || 0,
          orgName: currentUser.organization,
          reason: reason,
          targetUser: reservation.requested_by_name // Pass the requesting user
        });
      }
      
      toast.success('Reservation rejected');
      await loadAllReservations(); // Reload all data
    } catch (err) {
      toast.error('Failed to reject reservation', { title: 'Rejection Error' });
    }
  };

  const handleComplete = async (reservationId) => {
    try {
      console.log('🎯 Starting reservation completion for:', reservationId);
      await completeReservation(reservationId, currentUser);
      console.log('✅ Reservation completion successful');
      
      // Find the reservation to get details for notification
      const reservation = reservations.find(r => r.id === reservationId);
      if (reservation && onNotifyUser) {
        onNotifyUser('reservation_ready', {
          propCount: reservation.reservation_items?.length || 0,
          orgName: currentUser.organization,
          targetUser: reservation.requested_by_name // Pass the requesting user
        });
      }
      
      toast.success('Reservation completed! Props are now checked out.');
      
      // Refresh both reservation data and main app data
      await loadAllReservations();
      
      // Trigger main app data refresh if callback provided
      if (onDataChange) {
        console.log('🔄 Triggering main app data refresh...');
        onDataChange();
      }
      
    } catch (err) {
      console.error('❌ Error completing reservation:', err);
      toast.error('Failed to complete reservation', { title: 'Completion Error' });
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'active_checkout': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'active_checkout': return <Package className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // Format time to 12-hour format with AM/PM
  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    // Handle various time formats
    const time = timeString.includes(':') ? timeString : timeString;
    
    try {
      // Parse the time string
      const [hours, minutes] = time.split(':');
      const hour24 = parseInt(hours, 10);
      const min = minutes || '00';
      
      // Convert to 12-hour format
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      
      return `${hour12}:${min} ${ampm}`;
    } catch (error) {
      // If parsing fails, return original string
      return timeString;
    }
  };
  const toggleReservationExpansion = (reservationId) => {
    const newExpanded = new Set(expandedReservations);
    if (newExpanded.has(reservationId)) {
      newExpanded.delete(reservationId);
    } else {
      newExpanded.add(reservationId);
    }
    setExpandedReservations(newExpanded);
  };

  const tabs = [
    { id: 'pending', label: 'Pending Requests', count: reservations.filter(r => r.status === 'pending').length },
    { id: 'ready', label: 'Ready for Pickup', count: reservations.filter(r => r.status === 'approved').length },
    { id: 'active', label: 'Active Checkouts', count: reservations.filter(r => r.status === 'active_checkout').length },
    { id: 'history', label: 'History', count: reservations.filter(r => ['completed', 'rejected', 'cancelled'].includes(r.status)).length }
  ];

  const stats = [
    <ViewLayout.Stat 
      title="Pending" 
      value={reservations.filter(r => r.status === 'pending').length} 
      icon={Clock} 
      color="orange" 
    />,
    <ViewLayout.Stat 
      title="Ready" 
      value={reservations.filter(r => r.status === 'approved').length} 
      icon={CheckCircle} 
      color="green" 
    />,
    <ViewLayout.Stat 
      title="Active" 
      value={reservations.filter(r => r.status === 'active_checkout').length} 
      icon={Package} 
      color="blue" 
    />,
    <ViewLayout.Stat 
      title="Total" 
      value={reservations.length} 
      icon={Calendar} 
      color="purple" 
    />
  ];

  const filteredReservations = getFilteredReservations();

  return (
    <ViewLayout
      title="Reservations Management"
      subtitle={currentUser.organization}
      stats={stats}
    >
      <div className="space-y-6">
        {/* Responsive Tabs */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="-mb-px flex space-x-2 sm:space-x-8 min-w-max">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                {tab.count > 0 && (
                  <span className={`ml-1 sm:ml-2 py-0.5 px-1 sm:px-2 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading reservations...</p>
        </div>
      )}

      {/* Reservations list - Responsive and contained */}
      {!loading && (
        <div className="space-y-2">
          {filteredReservations.length > 0 ? (
            filteredReservations.map(reservation => {
              const isExpanded = expandedReservations.has(reservation.id);
              return (
                <div key={reservation.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                  {/* Compact header - always visible */}
                  <div 
                    className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleReservationExpansion(reservation.id)}
                  >
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                          getStatusColor(reservation.status)
                        }`}>
                          {getStatusIcon(reservation.status)}
                          <span className="hidden sm:inline">
                            {reservation.status === 'approved' ? 'Ready' : 
                             reservation.status === 'active_checkout' ? 'Active' :
                             reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1)}
                          </span>
                        </div>
                        <div className="font-medium text-sm truncate">{reservation.requested_by_name}</div>
                        <div className="text-xs text-gray-500 hidden sm:inline">•</div>
                        <div className="text-xs text-gray-600 truncate hidden sm:block">{reservation.requesting_org?.name}</div>
                        <div className="text-xs text-gray-500 hidden sm:inline">•</div>
                        <div className="text-xs text-gray-500 flex-shrink-0">
                          {reservation.reservation_items?.length || 0} props
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        {/* Action buttons based on status */}
                        {reservation.status === 'pending' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedReservation(reservation);
                                setShowApprovalModal(true);
                              }}
                              className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedReservation(reservation);
                                setShowRejectionModal(true);
                              }}
                              className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {reservation.status === 'approved' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCompletionReservation(reservation);
                              setShowCompletionModal(true);
                            }}
                            className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 whitespace-nowrap"
                          >
                            Complete
                          </button>
                        )}
                        
                        {/* Expand/collapse indicator */}
                        <div className="text-gray-400">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded details - only visible when expanded */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 mt-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          <div>
                            <div className="font-medium text-sm">{reservation.requested_by_name}</div>
                            <div className="text-xs text-gray-600">{reservation.requesting_org?.name}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <div>
                            <div className="font-medium text-sm">Expected Return</div>
                            <div className="text-xs text-gray-600">
                              {new Date(reservation.expected_return_date).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <div>
                            <div className="font-medium text-sm">Purpose</div>
                            <div className="text-xs text-gray-600">{reservation.purpose || 'General Use'}</div>
                          </div>
                        </div>
                      </div>

                      {/* Props list with individual statuses */}
                      <div className="mb-3">
                        <h4 className="font-medium text-sm mb-2">Requested Props ({reservation.reservation_items?.length || 0}):</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {reservation.reservation_items?.map(item => {
                            // Find the current prop to get its live status
                            const currentProp = props.find(p => p.id === item.prop_id);
                            const propStatus = currentProp?.status || 'unknown';
                            
                            const getStatusColor = (status) => {
                              switch(status) {
                                case 'available': return 'bg-green-100 text-green-800';
                                case 'checked_out': return 'bg-orange-100 text-orange-800';
                                case 'reserved': return 'bg-purple-100 text-purple-800';
                                case 'under_repair': return 'bg-red-100 text-red-800';
                                default: return 'bg-gray-100 text-gray-800';
                              }
                            };
                            
                            return (
                              <div key={item.prop_id} className="flex items-center gap-2 text-xs p-2 bg-gray-50 rounded">
                                <Package className="h-3 w-3 text-gray-500" />
                                <div className="flex-1">
                                  <span className="font-medium">{item.props?.name || `Prop ${item.prop_id}`}</span>
                                  <span className="text-gray-500"> • {item.props?.category}</span>
                                </div>
                                <span className={`px-1 py-0.5 rounded text-xs font-medium ${getStatusColor(propStatus)}`}>
                                  {propStatus}
                                </span>
                              </div>
                            );
                          }) || (
                            <div className="text-xs text-gray-500 col-span-full">No props listed</div>
                          )}
                        </div>
                      </div>

                      {/* Notes */}
                      {reservation.notes && (
                        <div className="mb-3 p-2 bg-gray-50 rounded">
                          <div className="font-medium text-xs mb-1">Notes:</div>
                          <div className="text-xs text-gray-700">{reservation.notes}</div>
                        </div>
                      )}

                      {/* User's pickup preferences - IMPORTANT FOR ADMIN */}
                      {(reservation.preferred_pickup_day || reservation.preferred_time_range) && (
                        <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                          <div className="font-medium text-xs mb-1 text-blue-900">📅 User's Preferred Pickup:</div>
                          <div className="text-xs text-blue-800 space-y-1">
                            {reservation.preferred_pickup_day && (
                              <div><span className="font-medium">Day:</span> {reservation.preferred_pickup_day}</div>
                            )}
                            {reservation.preferred_time_range && (
                              <div><span className="font-medium">Time:</span> {reservation.preferred_time_range}</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Approval details for approved/completed reservations */}
                      {(reservation.status === 'approved' || reservation.status === 'completed') && reservation.approved_pickup_date && (
                        <div className="mb-3 p-2 bg-green-50 rounded">
                          <div className="font-medium text-xs mb-1">Approved Pickup:</div>
                          <div className="text-xs text-green-700">
                            {new Date(reservation.approved_pickup_date).toLocaleDateString()}
                            {reservation.approved_pickup_time && ` at ${formatTime(reservation.approved_pickup_time)}`}
                          </div>
                          {reservation.admin_notes && (
                            <div className="text-xs text-green-600 mt-1">{reservation.admin_notes}</div>
                          )}
                        </div>
                      )}

                      {/* Rejection reason for rejected reservations */}
                      {reservation.status === 'rejected' && reservation.admin_notes && (
                        <div className="p-2 bg-red-50 rounded">
                          <div className="font-medium text-xs mb-1">Rejection Reason:</div>
                          <div className="text-xs text-red-700">{reservation.admin_notes}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">No {activeTab} reservations</p>
              <p className="text-sm">
                {activeTab === 'pending' 
                  ? 'New reservation requests will appear here'
                  : activeTab === 'ready'
                  ? 'Approved reservations ready for pickup will appear here'
                  : activeTab === 'active'
                  ? 'Active checkouts will appear here'
                  : 'Completed, rejected, and cancelled reservations will appear here'
                }
              </p>
            </div>
          )}
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-semibold text-lg mb-4">Approve Reservation Request</h3>
            
            {/* Show user's preferred pickup */}
            {(selectedReservation.preferred_pickup_day || selectedReservation.preferred_time_range) && (
              <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                <div className="font-medium text-sm mb-2 text-blue-900">📅 User's Preferred Pickup:</div>
                <div className="text-sm text-blue-800 space-y-1">
                  {selectedReservation.preferred_pickup_day && (
                    <div><span className="font-medium">Day:</span> {selectedReservation.preferred_pickup_day}</div>
                  )}
                  {selectedReservation.preferred_time_range && (
                    <div><span className="font-medium">Time:</span> {selectedReservation.preferred_time_range}</div>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Approved Pickup Date *</label>
                <input
                  type="date"
                  id="pickup-date"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  min={new Date().toISOString().split('T')[0]}
                  defaultValue={selectedReservation.preferred_pickup_day || ''}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Use their preferred date or select an alternative
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Approved Pickup Time</label>
                <input
                  type="time"
                  id="pickup-time"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  defaultValue={selectedReservation.preferred_time_range || ''}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Use their preferred time or suggest an alternative
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Approval Notes</label>
                <textarea
                  id="approval-notes"
                  className="w-full border border-gray-300 rounded px-3 py-2 h-20"
                  placeholder="Any special instructions, alternative times, or pickup details..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const pickupDate = document.getElementById('pickup-date').value;
                    const pickupTime = document.getElementById('pickup-time').value;
                    const notes = document.getElementById('approval-notes').value;
                    
                    if (!pickupDate) {
                      toast.warning('Please select a pickup date');
                      return;
                    }
                    
                    handleApprove(selectedReservation.id, {
                      approved_pickup_date: pickupDate,
                      approved_pickup_time: pickupTime,
                      admin_notes: notes
                    });
                  }}
                  className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700"
                >
                  Approve
                </button>
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedReservation(null);
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Completion Modal */}
      {showCompletionModal && selectedCompletionReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-semibold text-lg mb-4 text-blue-800">Complete Reservation</h3>
            
            {/* Show reservation details */}
            <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
              <div className="font-medium text-sm mb-2 text-blue-900">✅ Completing checkout for:</div>
              <div className="text-sm text-blue-800 space-y-1">
                <div><span className="font-medium">Requester:</span> {selectedCompletionReservation.requested_by_name}</div>
                <div><span className="font-medium">Organization:</span> {selectedCompletionReservation.requesting_org?.name}</div>
                <div><span className="font-medium">Props:</span> {selectedCompletionReservation.reservation_items?.length || 0} items</div>
                <div><span className="font-medium">Expected return:</span> {new Date(selectedCompletionReservation.expected_return_date).toLocaleDateString()}</div>
                {selectedCompletionReservation.approved_pickup_date && (
                  <div><span className="font-medium">Approved pickup:</span> {new Date(selectedCompletionReservation.approved_pickup_date).toLocaleDateString()}</div>
                )}
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
              <div className="text-sm text-yellow-800">
                <div className="font-medium mb-1">⚠️ This action will:</div>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Mark all props as "checked out"</li>
                  <li>Change reservation status to "active checkout"</li>
                  <li>Create checkout history records</li>
                  <li>Send notification to the requester</li>
                </ul>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  handleComplete(selectedCompletionReservation.id);
                  setShowCompletionModal(false);
                  setSelectedCompletionReservation(null);
                }}
                className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
              >
                Complete Checkout
              </button>
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  setSelectedCompletionReservation(null);
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Rejection Modal */}
      {showRejectionModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-semibold text-lg mb-4 text-red-800">Reject Reservation Request</h3>
            
            {/* Show user's request details */}
            <div className="mb-4 p-3 bg-red-50 rounded border border-red-200">
              <div className="font-medium text-sm mb-2 text-red-900">🚫 Rejecting request from:</div>
              <div className="text-sm text-red-800 space-y-1">
                <div><span className="font-medium">Requester:</span> {selectedReservation.requested_by_name}</div>
                <div><span className="font-medium">Organization:</span> {selectedReservation.requesting_org?.name}</div>
                <div><span className="font-medium">Props:</span> {selectedReservation.reservation_items?.length || 0} items</div>
                {selectedReservation.preferred_pickup_day && (
                  <div><span className="font-medium">Requested pickup:</span> {selectedReservation.preferred_pickup_day}</div>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-red-800">Reason for Rejection *</label>
                <textarea
                  id="rejection-reason"
                  className="w-full border border-red-300 rounded px-3 py-2 h-24 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Explain why this request is being rejected. This message will be shared with the user..."
                  required
                />
                <div className="text-xs text-red-600 mt-1">
                  ⚠️ This reason will be visible to the user
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const reason = document.getElementById('rejection-reason').value.trim();
                    
                    if (!reason) {
                      toast.warning('Please provide a reason for rejection');
                      return;
                    }
                    
                    handleReject(selectedReservation.id, reason);
                    setShowRejectionModal(false);
                    setSelectedReservation(null);
                  }}
                  className="flex-1 bg-red-600 text-white py-2 rounded hover:bg-red-700"
                >
                  Confirm Rejection
                </button>
                <button
                  onClick={() => {
                    setShowRejectionModal(false);
                    setSelectedReservation(null);
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </ViewLayout>
  );
};

export default ReservationsView;